import { useEffect, useMemo } from "react";
import type { Question, Tab } from "../types";
import Group from "./Group";
import { isHidden } from "../util/rule";
import { useAnswers } from "../state/AnswersContext";
// Groups to suppress on a given tab regardless of their hide rules, declared by the
// running configurator (src/products/). Shared with the Save & Submit validation, so
// both agree on what is actually visible. For Natural Draft Burners the Customer
// Requirements tab (INPUTS_TAB) shows only Site & Unit Information + Sizing
// Requirements; for Flame Cells the empty Product Specifications group is dropped.
import { hiddenGroupCodes } from "../util/validation";
// FAH override: reveal the Main Burner / Pilot / Ignitor sections (collapsed)
// even though NetSuite hides them when no main burner is selected.
import { fahSectionHideState } from "../util/fahSections";
import { isOptionalGroupForLaunch } from "../util/quoteScope";
import { ensureFirstOpen, sectionModeOn } from "../util/sectionMode";

type Props = {
  tab: Tab;
  questions: Question[];
  /** Section mode (util/sectionMode.ts) applies to every tab after the first. */
  firstTab?: boolean;
};

export default function TabPanel({ tab, questions, firstTab = false }: Props) {
  const { answers, product } = useAnswers();
  const sections = sectionModeOn() && !firstTab;

  const orderedGroups = useMemo(() => {
    const suppressed = hiddenGroupCodes(product, tab.custrecord_cpqc_tab_code);
    return [...tab._groups]
      .filter((g) => !suppressed.has(g.custrecord_cpqc_group_code))
      .filter(
        (g) =>
          !isHidden(
            {
              hideRule: g.custrecord_cpqc_group_hide_rule,
              parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule,
            },
            // FAH sections evaluate against a state with the "no main burner"
            // signal cleared, so they show; every other group uses `answers`.
            fahSectionHideState(g.custrecord_cpqc_group_code, answers)
          )
      )
      .sort(
        (a, b) =>
          Number(a.custrecord_cpqc_group_sequence || 0) -
          Number(b.custrecord_cpqc_group_sequence || 0)
      );
  }, [tab, answers, product]);

  const questionsByGroup = useMemo(() => {
    const map = new Map<string, Question[]>();
    for (const q of questions) {
      if (q.custrecord_cpqc_question_tab !== tab.id) continue;
      const gid = q.custrecord_cpqc_question_group;
      const list = map.get(gid) ?? [];
      list.push(q);
      map.set(gid, list);
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          Number(a.custrecord_cpqc_question_sequence || 0) -
          Number(b.custrecord_cpqc_question_sequence || 0)
      );
    }
    return map;
  }, [questions, tab.id]);

  // A tab shown for the first time in section mode opens its first section for review.
  useEffect(() => {
    if (sections) ensureFirstOpen(orderedGroups[0]?.id, orderedGroups.map((g) => g.id));
  }, [sections, orderedGroups]);

  return (
    <section className="cpq-tabpanel" role="tabpanel">
      {orderedGroups.map((g, i) => (
        <Group
          key={g.id}
          group={g}
          questions={questionsByGroup.get(g.id) ?? []}
          section={sections}
          sectionNext={sections ? orderedGroups[i + 1]?.id ?? null : null}
          // On a QUOTE, everything outside Customer Requirements and Main Burner / Pilot /
          // Ignitor is shown but not required. The heading says so, and validation reads the
          // same rule -- see isOptionalGroupForLaunch.
          optional={isOptionalGroupForLaunch(
            tab.custrecord_cpqc_tab_code,
            g.custrecord_cpqc_group_code
          )}
        />
      ))}
    </section>
  );
}
